import { Component, forwardRef, inject, input } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms';
import { BuildingService } from '@artemis-software/wr-api';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { createForm } from '@sonofabit/ng-core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UniqueInternalNumberValidator } from '@/utils/validators';

@Component({
  standalone: false,
  selector: 'wr-building-internal-data-form',
  templateUrl: './building-internal-data-form.component.html',
  styleUrl: './building-internal-data-form.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BuildingInternalDataFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BuildingInternalDataFormComponent),
      multi: true,
    },
  ],
})
export class BuildingInternalDataFormComponent extends AbstractFormControl<string | null> implements Validator {
  private readonly buildingService = inject(BuildingService);

  readonly buildingId = input<string | undefined>(undefined);

  readonly form = createForm(({ field }) => {
    return {
      internalNumber: field({
        value: null as string | null,
        validators: [Validators.required],
        asyncValidators: [UniqueInternalNumberValidator.createValidator(this.buildingService, this.buildingId())],
      }),
    };
  });

  constructor() {
    super();
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(val => {
      this.value = val.internalNumber ?? null;
      this.onValidatorChange?.();
    });
  }

  override setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  override writeValue(value: string | null | undefined): void {
    super.writeValue(value ?? null);
    this.form.patchValue({ internalNumber: value ?? null }, { emitEvent: false });
  }

  override markAsTouched(): void {
    super.markAsTouched();
    this.form.markAllAsTouched();
  }

  hasError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.invalid && (control.dirty || control.touched));
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.form.invalid ? { internalDataInvalid: true } : null;
  }

  private onValidatorChange?: () => void;
  registerOnValidatorChange(fn: () => void): void { this.onValidatorChange = fn; }
}
